import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import React, { createContext, type HTMLAttributes, type ReactNode, useContext, useEffect } from 'react';
import classnames from 'src/util/classnames';
import { useIsMobile } from 'src/hooks/useIsMobile';
import { useMovableResizable } from 'src/hooks/useMovableResizable';
import Icon from '../Icon';

interface ModalProps {
  isOpen: boolean;
  toggle?: () => void;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  centered?: boolean;
  /** false renders a non-modal dialog: no dim overlay, no focus trap, and the page behind stays scrollable/clickable. */
  backdrop?: boolean | 'static';
  keyboard?: boolean;
  scrollable?: boolean;
  fullscreen?: boolean | string;
  /** Lets the user drag the modal by its ModalHeader. Default true; forced off for fullscreen modals and on mobile. */
  movable?: boolean;
  /** Lets the user resize the modal from its bottom-right corner. Default true; forced off for fullscreen modals and on mobile. */
  resizable?: boolean;
  className?: string;
  contentClassName?: string;
  modalClassName?: string;
  children?: ReactNode;
}

interface ModalFloatContextValue {
  headerRef: (node: HTMLElement | null) => void;
  draggable: boolean;
}

const ModalFloatContext = createContext<ModalFloatContextValue | null>(null);

export function Modal({
  isOpen = false,
  toggle,
  size,
  centered,
  backdrop = true,
  keyboard = true,
  scrollable,
  fullscreen,
  movable = true,
  resizable = true,
  className,
  contentClassName,
  modalClassName,
  children,
}: ModalProps) {
  const handleClose = () => {
    if (backdrop !== 'static' && toggle) toggle();
  };

  // A fullscreen modal already fills the viewport, and dragging/resizing is
  // impractical on small screens — both disable the feature regardless of props.
  const isMobile = useIsMobile();
  const canFloat = !fullscreen && !isMobile;
  const effectiveMovable = movable && canFloat;
  const effectiveResizable = resizable && canFloat;

  const { isFloating, pos, size: floatSize, panelRef, headerRef, gripRef } = useMovableResizable({
    movable: effectiveMovable,
    resizable: effectiveResizable,
    isOpen: !!isOpen,
  });

  const nonModal = backdrop === false;

  // HeadlessUI's Dialog has no public way to opt out of its own ESC handling,
  // so the non-modal path (which skips Dialog entirely) re-implements it here.
  useEffect(() => {
    if (!nonModal || !isOpen || !keyboard) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') toggle?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [nonModal, isOpen, keyboard, toggle]);

  const dialogClassName = classnames(
    'modal-dialog',
    !isFloating && centered && 'modal-dialog-centered',
    !isFloating && size === 'sm' && 'modal-sm',
    !isFloating && size === 'lg' && 'modal-lg',
    !isFloating && size === 'xl' && 'modal-xl',
    scrollable && 'modal-dialog-scrollable',
    fullscreen === true ? 'modal-fullscreen'
      : typeof fullscreen === 'string' ? `modal-fullscreen-${fullscreen}-down`
      : undefined,
    className,
  );

  const contentNode = (
    <ModalFloatContext.Provider value={{ headerRef, draggable: effectiveMovable }}>
      <div
        ref={panelRef}
        className={classnames('modal-content', isFloating && 'modal-content-floating', contentClassName)}
        style={isFloating ? { position: 'fixed', left: pos.x, top: pos.y, width: floatSize.w, height: floatSize.h } : undefined}
      >
        {children}
        {resizable && (
          <div ref={gripRef} className="modal-resize-grip" style={canFloat ? undefined : { display: 'none' }} />
        )}
      </div>
    </ModalFloatContext.Provider>
  );

  // Non-modal: no HeadlessUI Dialog (its scroll lock/focus trap can't be opted
  // out of), no backdrop, page behind stays scrollable and clickable.
  if (nonModal) {
    if (!isOpen) return null;
    return (
      <div
        className={classnames('modal block modal-nonmodal', modalClassName)}
        style={{ zIndex: 1060 }}
        tabIndex={-1}
        role="dialog"
        aria-modal="false"
      >
        <div className={dialogClassName} role="document">
          {contentNode}
        </div>
      </div>
    );
  }

  return (
    <Dialog open={!!isOpen} onClose={handleClose} transition>
      {/* Backdrop — emotion CSS handles opacity transition via &[data-closed] */}
      <DialogBackdrop
        transition
        className="modal-backdrop"
        style={{ zIndex: 1050 }}
      />

      {/* Modal overlay */}
      <div
        className={classnames('modal block', modalClassName)}
        style={{ zIndex: 1060 }}
        tabIndex={-1}
        role="dialog"
      >
        {/* Panel — emotion CSS handles scale+fade via &[data-closed] */}
        <DialogPanel transition className={dialogClassName} role="document">
          {contentNode}
        </DialogPanel>
      </div>
    </Dialog>
  );
}

export function ModalHeader({ toggle, tag, className, children, ...props }: HTMLAttributes<HTMLDivElement> & { toggle?: () => void; tag?: string }) {
  const Tag = (tag ?? 'div') as React.ElementType;
  const floatCtx = useContext(ModalFloatContext);
  return (
    <Tag
      ref={floatCtx?.headerRef}
      className={classnames('modal-header bg-primary text-white text-bold', floatCtx?.draggable && 'cursor-move', className)}
      {...props}
    >
      {children}
      {toggle && (
        <button type="button" data-modal-btn className="text-white cursor-pointer hover:scale-125 transition-transform duration-300 ease-in-out" aria-label="Close" onClick={toggle}><Icon icon="X" weight="regular" /></button>
      )}
    </Tag>
  );
}

export function ModalBody({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={classnames('modal-body', className)} {...props}>
      {children}
    </div>
  );
}

export function ModalFooter({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={classnames('modal-footer', className)} {...props}>
      {children}
    </div>
  );
}
